import type {
    ContentReferenceStatus,
    IntegrationCategory,
    TableCellTarget,
} from '@gitbook/api-client';
import { arrayOfAll, assertNever } from '@gitbook/util-ts';

import type {
    IntegrationsProvider,
    NavigationScreenChangeRequestsFilters,
    NavigationScreenSpaceState,
} from './types';

export interface NavigationChangeRequestsSideSheet {
    type: 'change-requests';
    tab?: undefined;
    filters?: NavigationScreenChangeRequestsFilters;
}

export interface NavigationPageVariablesSideSheet {
    type: 'variables';
    tab?: 'page' | 'space';
}

export interface NavigationGitSyncSideSheet {
    type: 'gitsync';
    tab?: 'status' | 'progress';
}

export interface NavigationGitSyncSetupSideSheet {
    type: 'gitsync-setup';
    tab?: undefined;
}

export interface NavigationShareSideSheet {
    type: 'share';
    tab?: 'invite' | 'docs-sites';
}

export interface NavigationMergeRulesSideSheet {
    type: 'merge-rule';
    tab?: undefined;
}

export interface NavigationCommentsSideSheet {
    type: 'comments';
    tab?: undefined;
    node?: string;
    context?: 'preview';
    comment?: string;
    tableCell?: TableCellTarget;
}

export interface NavigationChangeRequestInfoSideSheet {
    type: 'overview';
    tab?: undefined;
}

export interface NavigationChangeRequestAgentSideSheet {
    type: 'agent';
    tab?: undefined;
}

export interface NavigationChangeRequestReviewsSideSheet {
    type: 'reviews';
    tab?: undefined;
}

export interface NavigationPageOptionsSideSheet {
    type: 'page';
    tab?: 'layout' | 'visibility';
}

export interface NavigationAudienceSideSheet {
    type: 'audience';
    tab?: undefined;
}

export interface NavigationIntegrationsModal {
    type: 'integrations';
    tab?: undefined;
    view?:
        | {
              type: 'installations';
          }
        | {
              type: 'category';
              category?: IntegrationCategory;
              provider?: IntegrationsProvider;
              search?: string;
          }
        | { type: 'integration'; integration: string; state?: 'configure' };
}

export interface NavigationAuditsSideSheet {
    type: 'audits';
    tab?: undefined;
    node?: string;
    scope?: 'space' | 'change-request';
    status?: ContentReferenceStatus;
}

export interface NavigationPublishedSideSheet {
    type: 'published';
    tab?: undefined;
}

export interface NavigationPublishSideSheet {
    type: 'publish';
    tab?: undefined;
}

/**
 * Sidesheets for the Space screen (not including change requests)
 */
export type NavigationSpaceSideSheet =
    | NavigationChangeRequestsSideSheet
    | NavigationPageVariablesSideSheet
    | NavigationGitSyncSideSheet
    | NavigationGitSyncSetupSideSheet
    | NavigationShareSideSheet
    | NavigationCommentsSideSheet
    | NavigationPageOptionsSideSheet
    | NavigationIntegrationsModal
    | NavigationAuditsSideSheet
    | NavigationMergeRulesSideSheet;

/**
 * Sidesheets for the Space screen when viewing a change request
 */
export type NavigationChangeRequestSideSheet =
    | NavigationPageVariablesSideSheet
    | NavigationChangeRequestInfoSideSheet
    | NavigationCommentsSideSheet
    | NavigationPageOptionsSideSheet
    | NavigationShareSideSheet
    | NavigationAuditsSideSheet
    | NavigationIntegrationsModal
    | NavigationMergeRulesSideSheet
    | NavigationChangeRequestAgentSideSheet;

/**
 * Sidesheets for the Collection screen
 */
export type NavigationCollectionSideSheet = NavigationShareSideSheet;

/**
 * Sidesheets for the Site screen
 */
export type NavigationSiteSideSheet =
    | NavigationIntegrationsModal
    | NavigationAudienceSideSheet
    | NavigationPublishedSideSheet
    | NavigationPublishSideSheet;

/**
 * A utility type that represents all possible sidesheets.
 */
export type AllSideSheets =
    | NavigationSpaceSideSheet
    | NavigationChangeRequestSideSheet
    | NavigationCollectionSideSheet
    | NavigationSiteSideSheet;

/**
 * A utility type that represents all possible sidesheets with tabs.
 */
export type SideSheetsWithTabs = Extract<AllSideSheets, { tab?: string | undefined }>;

/**
 * A union of all sidesheet types.
 */
export type NavigationSideSheetType = AllSideSheets['type'];

/**
 * Returns the keys of all possible tabs for a given sidesheet type.
 */
export function getNavigationSideSheetTabs(
    type: NavigationSideSheetType
): TabKeys<AllSideSheets['tab']>[] {
    switch (type) {
        case 'gitsync':
            return arrayOfAll<TabKeys<NavigationGitSyncSideSheet['tab']>>()(['status', 'progress']);
        case 'gitsync-setup':
            return [];
        case 'share':
            return arrayOfAll<TabKeys<NavigationShareSideSheet['tab']>>()(['invite', 'docs-sites']);
        case 'page':
            return arrayOfAll<TabKeys<NavigationPageOptionsSideSheet['tab']>>()([
                'layout',
                'visibility',
            ]);
        case 'variables':
            return arrayOfAll<TabKeys<NavigationPageVariablesSideSheet['tab']>>()([
                'page',
                'space',
            ]);
        case 'change-requests':
        case 'agent':
        case 'overview':
        case 'comments':
        case 'audits':
        case 'audience':
        case 'published':
        case 'publish':
        case 'merge-rule':
        case 'integrations':
            return [];
        default:
            assertNever(type);
    }
}

/**
 * The keys of all possible sidesheets in a change request screen.
 */
const CHANGE_REQUEST_SIDESHEET_KEYS = arrayOfAll<NavigationChangeRequestSideSheet['type']>()([
    'comments',
    'overview',
    'page',
    'variables',
    'share',
    'audits',
    'integrations',
    'agent',
    'merge-rule',
]);

/**
 * The keys of all possible sidesheets in a space screen.
 */
const SPACE_SIDESHEET_KEYS = arrayOfAll<NavigationSpaceSideSheet['type']>()([
    'change-requests',
    'gitsync',
    'gitsync-setup',
    'share',
    'comments',
    'page',
    'variables',
    'integrations',
    'audits',
    'merge-rule',
]);

/**
 * Get the keys of all possible sidesheets in a space screen
 */
export function getSpaceSideSheetKeys(
    screen: NavigationScreenSpaceState
): Array<NavigationSpaceSideSheet['type'] | NavigationChangeRequestSideSheet['type']> {
    if (screen.revision) {
        return [];
    }

    return screen.changeRequest ? CHANGE_REQUEST_SIDESHEET_KEYS : SPACE_SIDESHEET_KEYS;
}

/**
 * The keys of all possible sidesheets in a collection screen.
 */
export const COLLECTION_SIDESHEET_KEYS = arrayOfAll<NavigationCollectionSideSheet['type']>()([
    'share',
]);

export const SITE_SIDESHEET_KEYS = arrayOfAll<NavigationSiteSideSheet['type']>()([
    'audience',
    'integrations',
    'published',
    'publish',
]);

/**
 * The keys of all possible sidesheets.
 */
export const ALL_SIDESHEET_KEYS = arrayOfAll<AllSideSheets['type']>()([
    ...SITE_SIDESHEET_KEYS,
    ...SPACE_SIDESHEET_KEYS,
    ...CHANGE_REQUEST_SIDESHEET_KEYS,
    ...COLLECTION_SIDESHEET_KEYS,
]);

/**
 * SideSheet navigation state allows for "tab" to be undefined. This utility type
 * can be used to remove undefined from the ScreenState['tab'] type.
 */
export type TabKeys<Keys> = Exclude<Keys, undefined>;
